import React, { useState, useEffect, useMemo } from "react";
import { useIsMobile } from "@/hooks/useMobile";
import { createPortal } from "react-dom";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Checkbox } from "../ui/checkbox";
import {
	Calendar as CalendarIcon,
	Clock,
	Loader2,
	X,
	Pause,
	Trash2,
	ChevronDown,
	AlertTriangle,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";
import type {
	Channel,
	ChannelPublicationTime,
} from "@/services/business/calendar";
import { getChannelIcon, getChannelLabel } from "@/utils/channels";
import { validateMultihookOrder } from "@/components/modals/content-detail/types";

interface SocialConnection {
	id: string;
	user_id: string;
	platform: string;
	connected: boolean;
	username: string | null;
	created_at: string;
	updated_at: string;
}

interface ContentScheduleModalProps {
	isOpen: boolean;
	onClose: () => void;
	contentTitle: string;
	publications: ChannelPublicationTime[];
	connectedPlatforms: SocialConnection[];
	onSave: (publications: ChannelPublicationTime[]) => Promise<boolean>;
	isMultihook?: boolean;
	/** Called when user clicks "Valider et lancer maintenant" in AB test warning */
	onValidateAndLaunch?: (publications: ChannelPublicationTime[]) => void;
}

const MULTIHOOK_MIN_HOURS = 24;

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
	const h = Math.floor(i / 2);
	const m = (i % 2) * 30;
	return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
});

function TimeSelect({
	value,
	onChange,
}: Readonly<{ value: string; onChange: (v: string) => void }>) {
	const [open, setOpen] = useState(false);
	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs transition-colors"
					style={{
						border: "1px solid hsl(var(--dash-border))",
						color: "hsl(var(--dash-text-secondary))",
					}}
				>
					<Clock
						className="h-3.5 w-3.5"
						style={{ color: "hsl(var(--dash-accent))" }}
					/>
					<span>{value}</span>
					<ChevronDown className="h-3 w-3 opacity-50" />
				</button>
			</PopoverTrigger>
			<PopoverContent
				className="w-28 p-1 bg-[#141414] border border-[#262626] z-[10001] max-h-48 overflow-y-auto"
				align="start"
				side="bottom"
			>
				{TIME_OPTIONS.map((t) => (
					<button
						key={t}
						onClick={() => {
							onChange(t);
							setOpen(false);
						}}
						className={`w-full text-left px-3 py-1.5 text-xs rounded transition-colors ${t === value ? "bg-[#292929] text-white" : "text-[#999] hover:bg-[#1f1f1f] hover:text-white"}`}
					>
						{t}
					</button>
				))}
			</PopoverContent>
		</Popover>
	);
}

export function ContentScheduleModal({
	isOpen,
	onClose,
	contentTitle,
	publications,
	connectedPlatforms,
	onSave,
	isMultihook = false,
	onValidateAndLaunch,
}: Readonly<ContentScheduleModalProps>) {
	const isMobile = useIsMobile();
	const [editedPublications, setEditedPublications] = useState<
		ChannelPublicationTime[]
	>([]);
	const [allNetworks, setAllNetworks] = useState(false);
	const [validationError, setValidationError] = useState<string | null>(null);
	const [pausedPlatforms, setPausedPlatforms] = useState<Set<Channel>>(
		new Set(),
	);
	const [abTestWarning, setAbTestWarning] = useState<{
		timeRemaining: string;
		pendingPublications: ChannelPublicationTime[];
	} | null>(null);
	const [youtubeOverride, setYoutubeOverride] = useState<{
		date: Date;
		time: string;
	} | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const [youtubeActive, setYoutubeActive] = useState(true);
	const [youtubeDateOpen, setYoutubeDateOpen] = useState(false);

	const [globalDate, setGlobalDate] = useState<Date>(new Date());
	const [globalTime, setGlobalTime] = useState<string>("09:00");
	const [globalDateOpen, setGlobalDateOpen] = useState(false);
	const [individualDateOpen, setIndividualDateOpen] = useState<
		Record<string, boolean>
	>({});

	const connectedChannels = new Set(
		connectedPlatforms
			.filter((p) => p.connected)
			.map((p) => p.platform as Channel),
	);

	// Channels from existing publications — always editable even if not "connected"
	const publishedChannels = new Set(publications.map((p) => p.channel));

	const unconnectedPlatforms = connectedPlatforms.filter(
		(p) => !p.connected && !publishedChannels.has(p.platform as Channel),
	);

	// All channels that have existing publications are editable, plus connected ones
	const allEditableChannels = new Set([
		...connectedChannels,
		...publishedChannels,
	]);
	const editableChannels = isMultihook
		? new Set([...allEditableChannels].filter((c) => c !== "youtube"))
		: allEditableChannels;

	const calculatedYoutubeDate = useMemo(() => {
		if (!isMultihook) return null;
		const nonYt = editedPublications.filter((p) => p.channel !== "youtube");
		if (nonYt.length === 0) return null;
		let earliest = new Date(nonYt[0].date);
		const [h0, m0] = nonYt[0].time.split(":").map(Number);
		earliest.setHours(h0, m0, 0, 0);
		nonYt.forEach((pub) => {
			const d = new Date(pub.date);
			const [hh, mm] = pub.time.split(":").map(Number);
			d.setHours(hh, mm, 0, 0);
			if (d < earliest) earliest = d;
		});
		const ytDate = new Date(
			earliest.getTime() - MULTIHOOK_MIN_HOURS * 60 * 60 * 1000,
		);
		return {
			date: ytDate,
			time: `${String(ytDate.getHours()).padStart(2, "0")}:${String(ytDate.getMinutes()).padStart(2, "0")}`,
		};
	}, [editedPublications, isMultihook]);

	// Effective YouTube date: override takes priority over calculated
	const effectiveYoutubeDate = youtubeOverride || calculatedYoutubeDate;

	// AB test warning check is now performed only at save time (handleSave)

	useEffect(() => {
		if (!isOpen) return;

		const byChannel = new Map(publications.map((p) => [p.channel, p]));

		// Use functional update so we can read the previous state without adding it
		// to the dependency array. We union editableChannels with whatever channels
		// were already shown — this prevents a channel from disappearing when the
		// user disables it, saves, then reopens the modal (even if connectedPlatforms
		// hasn't fully loaded yet at render time).
		setEditedPublications((prev) => {
			const channelsToShow = new Set([
				...editableChannels,
				...prev.map((p) => p.channel),
			]);
			return [...channelsToShow].map(
				(ch) =>
					byChannel.get(ch) ??
					// Preserve the user's previously-entered date/time if available
					prev.find((p) => p.channel === ch) ??
					{ channel: ch, date: new Date(), time: "09:00" },
			);
		});

		// Channels not in the DB start paused; previously paused channels that
		// are still absent from the DB stay paused.
		// Channels explicitly saved with pubStatus 'paused' are also paused —
		// this is the persistent counterpart to the in-memory pausedPlatforms set.
		setPausedPlatforms((prevPaused) => {
			return new Set([
				...[...editableChannels].filter((ch) => !byChannel.has(ch)),
				...[...prevPaused].filter((ch) => !byChannel.has(ch)),
				...publications
					.filter((p) => p.pubStatus === "paused")
					.map((p) => p.channel),
			]);
		});

		setValidationError(null);
		setYoutubeOverride(null);
		setYoutubeActive(true);
		setAbTestWarning(null);
		const nonYt = publications.filter(
			(p) => !isMultihook || p.channel !== "youtube",
		);
		if (nonYt.length > 0) {
			setGlobalDate(nonYt[0].date || new Date());
			setGlobalTime(nonYt[0].time || "09:00");
		}
	}, [isOpen, publications, connectedPlatforms]);

	const validateMultihookDate = (date: Date, time: string): boolean => {
		if (!isMultihook) return true;
		const now = new Date();
		const scheduled = new Date(date);
		const [hours, minutes] = time.split(":").map(Number);
		scheduled.setHours(hours, minutes, 0, 0);
		return (
			(scheduled.getTime() - now.getTime()) / (1000 * 60 * 60) >=
			MULTIHOOK_MIN_HOURS
		);
	};

	const checkAbTestWarning = (
		updatedPublications: ChannelPublicationTime[],
	) => {
		if (!isMultihook || !allEditableChannels.has("youtube")) return;

		// Recalculate YouTube date from updated publications
		const nonYt = updatedPublications.filter((p) => p.channel !== "youtube");
		if (nonYt.length === 0) return;

		let earliest = new Date(nonYt[0].date);
		const [h0, m0] = nonYt[0].time.split(":").map(Number);
		earliest.setHours(h0, m0, 0, 0);
		nonYt.forEach((pub) => {
			const d = new Date(pub.date);
			const [hh, mm] = pub.time.split(":").map(Number);
			d.setHours(hh, mm, 0, 0);
			if (d < earliest) earliest = d;
		});

		const ytDate = youtubeOverride
			? new Date(youtubeOverride.date)
			: new Date(earliest);
		if (!youtubeOverride) {
			ytDate.setHours(ytDate.getHours() - MULTIHOOK_MIN_HOURS);
		} else {
			const [oh, om] = youtubeOverride.time.split(":").map(Number);
			ytDate.setHours(oh, om, 0, 0);
		}

		if (ytDate.getTime() < Date.now()) {
			let earliestMs = Infinity;
			for (const pub of nonYt) {
				const d = new Date(pub.date);
				const [hh, mm] = pub.time.split(":").map(Number);
				d.setHours(hh, mm, 0, 0);
				if (d.getTime() < earliestMs) earliestMs = d.getTime();
			}
			const remaining = earliestMs - Date.now();
			setAbTestWarning({
				timeRemaining: formatDuration(Math.max(0, remaining)),
				pendingPublications: updatedPublications.filter(
					(p) => !pausedPlatforms.has(p.channel),
				),
			});
		} else {
			setAbTestWarning(null);
		}
	};

	const handleUpdatePublication = (
		channel: Channel,
		field: "date" | "time",
		value: Date | string,
	) => {
		const updated = editedPublications.map((pub) =>
			pub.channel === channel ? { ...pub, [field]: value } : pub,
		);
		setEditedPublications(updated);
		setValidationError(null);
		checkAbTestWarning(updated);
	};

	const handleApplyToAll = () => {
		setEditedPublications((prev) =>
			prev.map((pub) => ({ ...pub, date: globalDate, time: globalTime })),
		);
		setValidationError(null);
	};

	useEffect(() => {
		if (allNetworks) handleApplyToAll();
	}, [allNetworks, globalDate, globalTime]);

	useEffect(() => {
		if (!isOpen) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [isOpen, onClose]);

	const togglePause = (channel: Channel) => {
		setPausedPlatforms((prev) => {
			const next = new Set(prev);
			if (next.has(channel)) next.delete(channel);
			else next.add(channel);
			return next;
		});
	};

	const handleRemovePublication = (channel: Channel) => {
		setEditedPublications((prev) => prev.filter((p) => p.channel !== channel));
	};

	const formatDuration = (ms: number): string => {
		const totalMinutes = Math.floor(ms / 60000);
		const h = Math.floor(totalMinutes / 60);
		const m = totalMinutes % 60;
		if (h > 0 && m > 0) return `${h}h${String(m).padStart(2, "0")}`;
		if (h > 0) return `${h}h`;
		return `${m}min`;
	};

	const buildToSave = (
		includeYoutube: boolean,
		youtubeNow: boolean,
	): ChannelPublicationTime[] => {
		// Include ALL publications: user-deactivated ones are marked 'paused' so they
		// persist in the DB and reappear correctly the next time the modal opens.
		const toSave = editedPublications.map((p) => ({
			...p,
			pubStatus: pausedPlatforms.has(p.channel)
				? "paused"
				: (p.pubStatus === "published" ? "published" : "scheduled"),
		}));
		if (
			isMultihook &&
			allEditableChannels.has("youtube") &&
			includeYoutube &&
			youtubeActive &&
			effectiveYoutubeDate
		) {
			const now = new Date();
			const ytDate = youtubeNow ? now : effectiveYoutubeDate.date;
			const ytTime = youtubeNow
				? `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`
				: effectiveYoutubeDate.time;
		toSave.push({
			channel: "youtube" as Channel,
			date: ytDate,
			time: ytTime,
			pubStatus: "scheduled",
		});
		}
		return toSave;
	};

	const handleSave = () => {
		if (isMultihook) {
			// Skip per-publication multihook validation — the A/B test warning popup below
			// handles the case when YouTube's calculated date falls in the past, offering
			// richer options (launch now, skip A/B test, cancel).
		}

		// If warning is already showing, don't save — user must choose an option
		if (abTestWarning) return;

		// Reject any publication scheduled in the past
		const now = Date.now();
		for (const pub of editedPublications) {
			if (pausedPlatforms.has(pub.channel) || pub.pubStatus === "published")
				continue;
			const scheduled = new Date(pub.date);
			const [hh, mm] = pub.time.split(":").map(Number);
			scheduled.setHours(hh, mm, 0, 0);
			if (scheduled.getTime() < now) {
				setValidationError(
					`La date de ${getChannelLabel(pub.channel)} est dans le passé`,
				);
				return;
			}
		}

		// Validate multihook order: YouTube (AB test) must be before all other platforms
		if (isMultihook && effectiveYoutubeDate && youtubeActive) {
			const ytDatetime = new Date(effectiveYoutubeDate.date);
			const [ytH, ytM] = effectiveYoutubeDate.time.split(":").map(Number);
			ytDatetime.setHours(ytH, ytM, 0, 0);

			const activeNonYtPubs = editedPublications.filter(
				(p) =>
					p.channel !== "youtube" &&
					!pausedPlatforms.has(p.channel) &&
					p.pubStatus !== "published",
			);
			const orderError = validateMultihookOrder(ytDatetime, activeNonYtPubs);
			if (orderError) {
				setValidationError(
					`La date YouTube (A/B test) doit être strictement avant toutes les autres plateformes. ` +
						`Elle entre en conflit avec ${getChannelLabel(orderError.offendingChannel)}.`,
				);
				return;
			}
		}

		// Check YouTube A/B test date at save time
		if (
			isMultihook &&
			effectiveYoutubeDate &&
			allEditableChannels.has("youtube")
		) {
			const ytScheduled = new Date(effectiveYoutubeDate.date);
			const [ytH, ytM] = effectiveYoutubeDate.time.split(":").map(Number);
			ytScheduled.setHours(ytH, ytM, 0, 0);

			if (ytScheduled.getTime() < Date.now()) {
				const nonYt = editedPublications.filter(
					(p) => !pausedPlatforms.has(p.channel),
				);
				let earliestMs = Infinity;
				for (const pub of nonYt) {
					const d = new Date(pub.date);
					const [hh, mm] = pub.time.split(":").map(Number);
					d.setHours(hh, mm, 0, 0);
					if (d.getTime() < earliestMs) earliestMs = d.getTime();
				}
				const remaining = earliestMs - Date.now();
				setAbTestWarning({
					timeRemaining: formatDuration(Math.max(0, remaining)),
					pendingPublications: editedPublications.filter(
						(p) => !pausedPlatforms.has(p.channel),
					),
				});
				return;
			}
		}

		const toSave = buildToSave(true, false);
		onSave(toSave);
		onClose();
	};

	const handleAbTestNow = () => {
		const toSave = buildToSave(true, true);
		if (onValidateAndLaunch) {
			onValidateAndLaunch(toSave);
		} else {
			onSave(toSave);
		}
		setAbTestWarning(null);
		onClose();
	};

	const handleSkipAbTest = () => {
		const toSave = buildToSave(false, false);
		onSave(toSave);
		setAbTestWarning(null);
		onClose();
	};

	return createPortal(
		<AnimatePresence>
			{isOpen && (
				<>
					{/* Backdrop */}
					<motion.div
						className="fixed inset-0"
						style={{
							zIndex: 9999,
							background: "rgba(10,10,10,0.95)",
							backdropFilter: "blur(8px)",
						}}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.15 }}
						onClick={(e) => {
							// Don't close if clicking inside a popover/select portal
							const target = e.target as HTMLElement;
							if (
								target.closest("[data-radix-popper-content-wrapper]") ||
								target.closest('[role="listbox"]')
							)
								return;
							onClose();
						}}
					/>

					{/* Close button - hidden on mobile, shown on desktop */}
					<button
						onClick={onClose}
						className="fixed top-6 right-6 text-white/40 hover:text-white/80 transition-colors hidden sm:block"
						style={{ zIndex: 10001 }}
					>
						<X className="h-6 w-6" />
					</button>

					{/* Content */}
					<motion.div
						className="fixed inset-0 flex items-center justify-center pointer-events-none v10-dashboard"
						style={{ zIndex: 10000 }}
						initial={{ opacity: 0, scale: 0.98 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.98 }}
						transition={{ duration: 0.25, delay: 0.05 }}
					>
						<dialog
							className="pointer-events-auto flex flex-col overflow-hidden w-full h-full sm:w-[50vw] sm:max-w-[700px] sm:min-w-[450px] sm:max-h-[85vh] sm:rounded-2xl sm:h-auto"
							style={{
								background: "hsl(var(--dash-card-bg))",
								border: "1px solid hsl(var(--dash-border))",
							}}
							onClick={(e) => e.stopPropagation()}
							onKeyDown={(e) => {
								if (e.key === "Escape") {
									e.preventDefault();
									onClose();
								}
							}}
							aria-labelledby="schedule-modal-title"
							open={isOpen}
						>
							{/* Header */}
							<div
								className="flex-shrink-0 px-6 pt-5 pb-4 relative"
								style={{ borderBottom: "1px solid hsl(var(--dash-border))" }}
							>
								{/* Close button - mobile only, inside dialog */}
								<button
									onClick={onClose}
									className="absolute top-4 right-4 text-white/40 hover:text-white/80 transition-colors sm:hidden"
								>
									<X className="h-5 w-5" />
								</button>
								<p
									id="schedule-modal-title"
									className="text-[10px] uppercase tracking-[0.15em] font-medium"
									style={{ color: "hsl(var(--dash-text-tertiary))" }}
								>
									Planification des publications
								</p>
							</div>

							{/* Scrollable body */}
							<div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
								{editedPublications.length === 0 &&
								unconnectedPlatforms.length === 0 ? (
									<div className="p-6 text-center">
										<p
											className="text-sm"
											style={{ color: "hsl(var(--dash-text-tertiary))" }}
										>
											Aucun réseau social connecté. Connectez vos réseaux pour
											planifier des publications.
										</p>
									</div>
								) : (
									<>
										{/* Checkbox "Appliquer à tous" — masqué en multihook car l'A/B test YouTube est toujours décalé */}
										{!isMultihook && (
											<div
												className="flex items-center gap-3 px-4 py-3 rounded-lg"
												style={{
													background: "hsl(var(--dash-bg-surface))",
													border: "1px solid hsl(var(--dash-border))",
												}}
											>
												<Checkbox
													id="all-networks"
													checked={allNetworks}
													onCheckedChange={(checked) =>
														setAllNetworks(checked as boolean)
													}
													className="border-white/30"
												/>
												<label
													htmlFor="all-networks"
													className="text-sm cursor-pointer flex-1"
													style={{ color: "hsl(var(--dash-text-secondary))" }}
												>
													Appliquer la même date/heure à tous
												</label>
											</div>
										)}

										{/* Global date/time when "apply to all" is checked */}
										{allNetworks && (
											<div className="flex items-center gap-3 px-4">
												<Popover
													open={globalDateOpen}
													onOpenChange={setGlobalDateOpen}
												>
													<PopoverTrigger asChild>
														<button
															className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs transition-colors"
															style={{
																border: "1px solid hsl(var(--dash-border))",
																color: "hsl(var(--dash-text-secondary))",
															}}
														>
															<CalendarIcon
																className="h-3.5 w-3.5"
																style={{ color: "hsl(var(--dash-accent))" }}
															/>
															{format(globalDate, "dd/MM/yy")}
														</button>
													</PopoverTrigger>
													<PopoverContent
														className="w-auto p-0 bg-[#141414] border border-[#262626] z-[10001]"
														align="start"
														side="bottom"
														onPointerDownOutside={(e) => e.preventDefault()}
														onInteractOutside={(e) => e.preventDefault()}
													>
														<Calendar
															mode="single"
															selected={globalDate}
															onSelect={(date) => {
																if (date) {
																	setGlobalDate(date);
																	setGlobalDateOpen(false);
																}
															}}
															disabled={{ before: new Date() }}
															initialFocus
															className="pointer-events-auto rounded-lg border-0"
														/>
													</PopoverContent>
												</Popover>
												<TimeSelect
													value={globalTime}
													onChange={setGlobalTime}
												/>
											</div>
										)}

										{/* AB Testing YouTube banner */}
										{isMultihook &&
											allEditableChannels.has("youtube") &&
											effectiveYoutubeDate && (
												<div
													className={`rounded-lg px-4 py-3 space-y-2 transition-opacity ${!youtubeActive ? "opacity-40" : ""}`}
													style={{
														border: "1px solid rgba(168,85,247,0.3)",
														background: "rgba(168,85,247,0.05)",
													}}
												>
													<div className="flex items-center gap-3">
														<button
															onClick={() => setYoutubeActive((prev) => !prev)}
															className="flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center transition-all"
															style={{
																background: youtubeActive
																	? "hsl(142, 71%, 45%)"
																	: "transparent",
																border: youtubeActive
																	? "1px solid hsl(142, 71%, 45%)"
																	: "1px solid hsl(var(--dash-border))",
															}}
														>
															{youtubeActive && (
																<svg
																	className="w-3 h-3 text-white"
																	fill="none"
																	viewBox="0 0 24 24"
																	stroke="currentColor"
																	strokeWidth={3}
																>
																	<path
																		strokeLinecap="round"
																		strokeLinejoin="round"
																		d="M5 13l4 4L19 7"
																	/>
																</svg>
															)}
														</button>
														<div className="flex-1">
															<div className="flex items-center justify-between">
																<div className="flex items-center gap-2">
																	<span
																		className="text-[10px] uppercase tracking-[0.15em] font-semibold"
																		style={{ color: "rgb(168,85,247)" }}
																	>
																		AB Testing (YouTube)
																	</span>
																	<span
																		className="text-[10px]"
																		style={{
																			color: "hsl(var(--dash-text-tertiary))",
																		}}
																	>
																		{youtubeOverride
																			? "• Date forcée manuellement"
																			: "• 24h avant la 1ère diffusion"}
																	</span>
																</div>
																{youtubeOverride && (
																	<button
																		onClick={() => {
																			setYoutubeOverride(null);
																			setValidationError(null);
																		}}
																		className="text-[10px] underline transition-colors"
																		style={{ color: "rgb(168,85,247)" }}
																	>
																		Recalculer auto
																	</button>
																)}
															</div>
														</div>
													</div>
													{youtubeActive && (
														<div
															className="flex items-center gap-3 text-xs pl-8"
															style={{
																color: "hsl(var(--dash-text-secondary))",
															}}
														>
															<Popover
																open={youtubeDateOpen}
																onOpenChange={setYoutubeDateOpen}
															>
																<PopoverTrigger asChild>
																	<button
																		className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs transition-colors"
																		style={{
																			border: "1px solid rgba(168,85,247,0.3)",
																			color: "hsl(var(--dash-text-secondary))",
																		}}
																	>
																		<CalendarIcon
																			className="h-3.5 w-3.5"
																			style={{ color: "rgb(168,85,247)" }}
																		/>
																		{format(
																			effectiveYoutubeDate.date,
																			"d MMM. yyyy",
																			{ locale: fr },
																		)}
																	</button>
																</PopoverTrigger>
																<PopoverContent
																	className="w-auto p-0 bg-[#141414] border border-[#262626] z-[10001]"
																	align="start"
																	side="bottom"
																>
																	<Calendar
																		mode="single"
																		selected={effectiveYoutubeDate.date}
																		onSelect={(date) => {
																			if (date) {
																				setYoutubeOverride({
																					date,
																					time: effectiveYoutubeDate.time,
																				});
																				setValidationError(null);
																				setYoutubeDateOpen(false);
																			}
																		}}
																		disabled={{ before: new Date() }}
																		initialFocus
																		className="pointer-events-auto rounded-lg border-0"
																	/>
																</PopoverContent>
															</Popover>
															<TimeSelect
																value={effectiveYoutubeDate.time}
																onChange={(t) => {
																	setYoutubeOverride({
																		date: effectiveYoutubeDate.date,
																		time: t,
																	});
																	setValidationError(null);
																}}
															/>
														</div>
													)}
												</div>
											)}

										{/* Table */}
										{editedPublications.length > 0 && (
											<div
												className="rounded-lg overflow-hidden"
												style={{ border: "1px solid hsl(var(--dash-border))" }}
											>
												{/* Table header - hidden on mobile */}
												<div
													className="hidden sm:grid grid-cols-[auto_1fr_auto_auto] gap-x-4 px-4 py-2"
													style={{
														background: "hsl(var(--dash-bg-surface))",
														borderBottom: "1px solid hsl(var(--dash-border))",
													}}
												>
													<span
														className="text-[10px] uppercase tracking-[0.15em] w-[32px]"
														style={{ color: "hsl(var(--dash-text-tertiary))" }}
													>
														Actif
													</span>
													<span
														className="text-[10px] uppercase tracking-[0.15em]"
														style={{ color: "hsl(var(--dash-text-tertiary))" }}
													>
														Plateforme
													</span>
													<span
														className="text-[10px] uppercase tracking-[0.15em] w-[100px]"
														style={{ color: "hsl(var(--dash-text-tertiary))" }}
													>
														Date
													</span>
													<span
														className="text-[10px] uppercase tracking-[0.15em] w-[100px]"
														style={{ color: "hsl(var(--dash-text-tertiary))" }}
													>
														Heure
													</span>
												</div>

												{/* Table rows */}
												{editedPublications.map((pub) => {
													const Icon = getChannelIcon(pub.channel);
													const isPaused = pausedPlatforms.has(pub.channel);
													const isPublished = pub.pubStatus === "published";
													const isActive = !isPaused && !isPublished;
													const isOrphaned = !connectedChannels.has(
														pub.channel,
													);

													const activeToggle = isPublished ? (
														<span
															className="flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center"
															style={{
																background: "hsl(var(--dash-border))",
																border: "1px solid hsl(var(--dash-border))",
															}}
														>
															<svg
																className="w-3 h-3"
																style={{
																	color: "hsl(var(--dash-text-tertiary))",
																}}
																fill="none"
																viewBox="0 0 24 24"
																stroke="currentColor"
																strokeWidth={3}
															>
																<path
																	strokeLinecap="round"
																	strokeLinejoin="round"
																	d="M5 13l4 4L19 7"
																/>
															</svg>
														</span>
													) : (
														<button
															onClick={() => togglePause(pub.channel)}
															className="flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center transition-all"
															style={{
																background: isActive
																	? "hsl(142, 71%, 45%)"
																	: "transparent",
																border: isActive
																	? "1px solid hsl(142, 71%, 45%)"
																	: "1px solid hsl(var(--dash-border))",
															}}
															title={isActive ? "Désactiver" : "Activer"}
														>
															{isActive && (
																<svg
																	className="w-3 h-3 text-white"
																	fill="none"
																	viewBox="0 0 24 24"
																	stroke="currentColor"
																	strokeWidth={3}
																>
																	<path
																		strokeLinecap="round"
																		strokeLinejoin="round"
																		d="M5 13l4 4L19 7"
																	/>
																</svg>
															)}
														</button>
													);

													const publishedBadge = isPublished ? (
														<span
															className="text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded"
															style={{
																background: "hsl(var(--dash-border))",
																color: "hsl(var(--dash-text-tertiary))",
															}}
														>
															Diffusé
														</span>
													) : null;

													const datePicker = isPublished ? (
														<span
															className={`text-xs ${isMobile ? "flex items-center justify-center px-3 py-2" : "px-3 py-1.5"}`}
															style={{
																color: "hsl(var(--dash-text-tertiary))",
															}}
														>
															{format(pub.date, "dd/MM/yy")}
														</span>
													) : (
														<Popover
															open={individualDateOpen[pub.channel]}
															onOpenChange={(open) =>
																setIndividualDateOpen((prev) => ({
																	...prev,
																	[pub.channel]: open,
																}))
															}
														>
															<PopoverTrigger asChild>
																<button
																	disabled={isPaused || allNetworks}
																	className={`flex items-center gap-1.5 rounded-lg px-3 text-xs transition-colors disabled:opacity-50 ${isMobile ? "flex-1 justify-center py-2" : "py-1.5"}`}
																	style={{
																		border: "1px solid hsl(var(--dash-border))",
																		color: "hsl(var(--dash-text-secondary))",
																	}}
																>
																	<CalendarIcon
																		className="h-3.5 w-3.5"
																		style={{ color: "hsl(var(--dash-accent))" }}
																	/>
																	{format(pub.date, "dd/MM/yy")}
																</button>
															</PopoverTrigger>
															<PopoverContent
																className="w-auto p-0 bg-[#141414] border border-[#262626] z-[10001]"
																align={isMobile ? "center" : "start"}
																side="bottom"
															>
																<Calendar
																	mode="single"
																	selected={pub.date}
																	onSelect={(date) => {
																		if (date) {
																			handleUpdatePublication(
																				pub.channel,
																				"date",
																				date,
																			);
																			setIndividualDateOpen((prev) => ({
																				...prev,
																				[pub.channel]: false,
																			}));
																		}
																	}}
																	disabled={{ before: new Date() }}
																	initialFocus
																	className="pointer-events-auto rounded-lg border-0"
																/>
															</PopoverContent>
														</Popover>
													);

													const timeControl =
														isPublished || isPaused || allNetworks ? (
															<span
																className={`text-xs ${isPublished ? "" : "opacity-50"} ${isMobile ? "flex items-center justify-center px-3 py-2" : "px-3 py-1.5"}`}
																style={{
																	color: "hsl(var(--dash-text-tertiary))",
																}}
															>
																{pub.time}
															</span>
														) : (
															<TimeSelect
																value={pub.time}
																onChange={(v) =>
																	handleUpdatePublication(
																		pub.channel,
																		"time",
																		v,
																	)
																}
															/>
														);

													return (
														<div
															key={pub.channel}
															className={`transition-opacity ${isPaused || isPublished ? "opacity-40" : ""}`}
															style={{
																borderBottom:
																	"1px solid hsl(var(--dash-border))",
															}}
														>
															{isMobile ? (
																<>
																	{/* Mobile card */}
																	<div className="px-4 py-3 space-y-2">
																		<div className="flex items-center gap-3">
																			{activeToggle}
																			<div
																				className={`flex items-center gap-2.5 ${isPaused ? "line-through" : ""}`}
																			>
																				<div
																					style={{
																						color:
																							"hsl(var(--dash-text-tertiary))",
																					}}
																				>
																					<Icon className="h-4 w-4" />
																				</div>
																				<span
																					className="text-sm font-medium"
																					style={{
																						color:
																							"hsl(var(--dash-text-secondary))",
																					}}
																				>
																					{getChannelLabel(pub.channel)}
																				</span>
																			</div>
																			{publishedBadge}
																		</div>
																		{isActive && (
																			<div className="flex items-center gap-2 pl-8">
																				{datePicker}
																				<div className="flex-1">
																					{timeControl}
																				</div>
																			</div>
																		)}
																		{isPublished && (
																			<div className="flex items-center gap-2 pl-8">
																				{datePicker}
																				{timeControl}
																			</div>
																		)}
																	</div>
																	<div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 items-center">
																		<div className="w-[100px]">
																			<Popover
																				open={
																					individualDateOpen[
																						`desktop_${pub.channel}`
																					]
																				}
																				onOpenChange={(open) =>
																					setIndividualDateOpen((prev) => ({
																						...prev,
																						[`desktop_${pub.channel}`]: open,
																					}))
																				}
																			>
																				<PopoverTrigger asChild>
																					<button
																						disabled={isPaused || allNetworks}
																						className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs transition-colors disabled:opacity-50"
																						style={{
																							border:
																								"1px solid hsl(var(--dash-border))",
																							color:
																								"hsl(var(--dash-text-secondary))",
																						}}
																					>
																						<CalendarIcon
																							className="h-3.5 w-3.5"
																							style={{
																								color:
																									"hsl(var(--dash-accent))",
																							}}
																						/>
																						{format(pub.date, "dd/MM/yy")}
																					</button>
																				</PopoverTrigger>
																				<PopoverContent
																					className="w-auto p-0 bg-[#141414] border border-[#262626] z-[10001]"
																					align="start"
																					side="bottom"
																					onPointerDownOutside={(e) =>
																						e.preventDefault()
																					}
																					onInteractOutside={(e) =>
																						e.preventDefault()
																					}
																				>
																					<Calendar
																						mode="single"
																						selected={pub.date}
																						onSelect={(date) => {
																							if (date) {
																								handleUpdatePublication(
																									pub.channel,
																									"date",
																									date,
																								);
																								setIndividualDateOpen(
																									(prev) => ({
																										...prev,
																										[`desktop_${pub.channel}`]: false,
																									}),
																								);
																							}
																						}}
																						initialFocus
																						className="pointer-events-auto rounded-lg border-0"
																					/>
																				</PopoverContent>
																			</Popover>
																		</div>
																		<div className="w-[100px]">
																			{isPaused || allNetworks ? (
																				<span
																					className="text-xs px-3 py-1.5 opacity-50"
																					style={{
																						color:
																							"hsl(var(--dash-text-tertiary))",
																					}}
																				>
																					{pub.time}
																				</span>
																			) : (
																				<TimeSelect
																					value={pub.time}
																					onChange={(v) =>
																						handleUpdatePublication(
																							pub.channel,
																							"time",
																							v,
																						)
																					}
																				/>
																			)}
																		</div>
																		<div className="w-[60px] flex items-center justify-end gap-1">
																			<button
																				onClick={() => togglePause(pub.channel)}
																				className="p-1.5 rounded-md transition-colors hover:bg-white/5"
																				style={{
																					color: isPaused
																						? "hsl(var(--dash-accent))"
																						: "hsl(var(--dash-text-tertiary))",
																				}}
																				title={
																					isPaused
																						? "Reprendre"
																						: "Mettre en pause"
																				}
																			>
																				<Pause className="h-4 w-4" />
																			</button>
																			{isOrphaned && (
																				<button
																					onClick={() =>
																						handleRemovePublication(pub.channel)
																					}
																					className="p-1.5 rounded-md transition-colors hover:bg-white/5"
																					style={{
																						color:
																							"hsl(var(--dash-text-tertiary))",
																					}}
																					title="Retirer"
																				>
																					<Trash2 className="h-4 w-4" />
																				</button>
																			)}
																		</div>
																	</div>

																	{/* Mobile card */}
																	<div className="sm:hidden px-4 py-3 space-y-2">
																		<div className="flex items-center justify-between">
																			<div
																				className={`flex items-center gap-2.5 ${isPaused ? "line-through" : ""}`}
																			>
																				<div
																					style={{
																						color:
																							"hsl(var(--dash-text-tertiary))",
																					}}
																				>
																					<Icon className="h-4 w-4" />
																				</div>
																				<span
																					className="text-sm"
																					style={{
																						color:
																							"hsl(var(--dash-text-secondary))",
																					}}
																				>
																					{getChannelLabel(pub.channel)}
																				</span>
																			</div>
																			<div className="w-[100px]">
																				{datePicker}
																			</div>
																			<div className="w-[100px]">
																				{timeControl}
																			</div>
																			<div className="w-[60px] flex items-center justify-end gap-1">
																				<button
																					onClick={() =>
																						togglePause(pub.channel)
																					}
																					className="p-1.5 rounded-md transition-colors hover:bg-white/5"
																					style={{
																						color: isPaused
																							? "hsl(var(--dash-accent))"
																							: "hsl(var(--dash-text-tertiary))",
																					}}
																					title={
																						isPaused
																							? "Reprendre"
																							: "Mettre en pause"
																					}
																				>
																					<Pause className="h-4 w-4" />
																				</button>
																				{isOrphaned && (
																					<button
																						onClick={() =>
																							handleRemovePublication(
																								pub.channel,
																							)
																						}
																						className="p-1.5 rounded-md transition-colors hover:bg-white/5"
																						style={{
																							color:
																								"hsl(var(--dash-text-tertiary))",
																						}}
																						title="Retirer"
																					>
																						<Trash2 className="h-4 w-4" />
																					</button>
																				)}
																			</div>
																		</div>
																		<div className="flex items-center gap-2">
																			<Popover
																				open={
																					individualDateOpen[
																						`mobile_${pub.channel}`
																					]
																				}
																				onOpenChange={(open) =>
																					setIndividualDateOpen((prev) => ({
																						...prev,
																						[`mobile_${pub.channel}`]: open,
																					}))
																				}
																			>
																				<PopoverTrigger asChild>
																					<button
																						disabled={isPaused || allNetworks}
																						className="flex-1 flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs transition-colors disabled:opacity-50"
																						style={{
																							border:
																								"1px solid hsl(var(--dash-border))",
																							color:
																								"hsl(var(--dash-text-secondary))",
																						}}
																					>
																						<CalendarIcon
																							className="h-3.5 w-3.5"
																							style={{
																								color:
																									"hsl(var(--dash-accent))",
																							}}
																						/>
																						{format(pub.date, "dd/MM/yy")}
																					</button>
																				</PopoverTrigger>
																				<PopoverContent
																					className="w-auto p-0 bg-[#141414] border border-[#262626] z-[10001]"
																					align="center"
																					side="bottom"
																					onPointerDownOutside={(e) =>
																						e.preventDefault()
																					}
																					onInteractOutside={(e) =>
																						e.preventDefault()
																					}
																				>
																					<Calendar
																						mode="single"
																						selected={pub.date}
																						onSelect={(date) => {
																							if (date) {
																								handleUpdatePublication(
																									pub.channel,
																									"date",
																									date,
																								);
																								setIndividualDateOpen(
																									(prev) => ({
																										...prev,
																										[`mobile_${pub.channel}`]: false,
																									}),
																								);
																							}
																						}}
																						initialFocus
																						className="pointer-events-auto rounded-lg border-0"
																					/>
																				</PopoverContent>
																			</Popover>
																			<div className="flex-1">
																				{isPaused || allNetworks ? (
																					<span
																						className="flex items-center justify-center text-xs px-3 py-2 opacity-50"
																						style={{
																							color:
																								"hsl(var(--dash-text-tertiary))",
																						}}
																					>
																						{pub.time}
																					</span>
																				) : (
																					<TimeSelect
																						value={pub.time}
																						onChange={(v) =>
																							handleUpdatePublication(
																								pub.channel,
																								"time",
																								v,
																							)
																						}
																					/>
																				)}
																			</div>
																		</div>
																	</div>
																</>
															) : (
																<div className="grid grid-cols-[auto_1fr_auto_auto] gap-x-4 items-center px-4 py-3">
																	<div className="w-[32px] flex items-center justify-center">
																		{activeToggle}
																	</div>
																	<div
																		className={`flex items-center gap-2.5 ${isPaused ? "line-through" : ""}`}
																	>
																		<div
																			style={{
																				color: "hsl(var(--dash-text-tertiary))",
																			}}
																		>
																			<Icon className="h-4 w-4" />
																		</div>
																		<span
																			className="text-sm"
																			style={{
																				color:
																					"hsl(var(--dash-text-secondary))",
																			}}
																		>
																			{getChannelLabel(pub.channel)}
																		</span>
																		{publishedBadge}
																	</div>
																	<div className="w-[100px]">{datePicker}</div>
																	<div className="w-[100px]">{timeControl}</div>
																</div>
															)}
														</div>
													);
												})}
											</div>
										)}

										{/* Add network section */}
										{unconnectedPlatforms.length > 0 && (
											<div
												className="rounded-lg px-4 py-3 space-y-2"
												style={{ border: "1px dashed hsl(var(--dash-border))" }}
											>
												<p
													className="text-[10px] uppercase tracking-[0.15em]"
													style={{ color: "hsl(var(--dash-text-tertiary))" }}
												>
													Ajouter un réseau
												</p>
												<div className="space-y-1.5">
													{unconnectedPlatforms.map((p) => {
														const Icon = getChannelIcon(p.platform as Channel);
														return (
															<div
																key={p.platform}
																className="flex items-center gap-2.5 py-1.5"
															>
																<div
																	style={{
																		color: "hsl(var(--dash-text-tertiary))",
																	}}
																>
																	<Icon className="h-4 w-4" />
																</div>
																<span
																	className="text-sm"
																	style={{
																		color: "hsl(var(--dash-text-tertiary))",
																	}}
																>
																	{getChannelLabel(p.platform as Channel)}
																</span>
																<span className="inline-block w-1.5 h-1.5 rounded-full ml-1 bg-gray-500" />
																<span
																	className="text-xs font-medium"
																	style={{ color: "hsl(var(--dash-accent))" }}
																>
																	Connecter
																</span>
															</div>
														);
													})}
												</div>
											</div>
										)}
									</>
								)}
							</div>

							{/* A/B Test Warning Dialog */}
							{abTestWarning && (
								<div
									className="fixed inset-0 z-[10002] flex items-center justify-center"
									style={{ background: "rgba(0,0,0,0.7)" }}
									onMouseDown={(e) => {
										if (e.target === e.currentTarget) setAbTestWarning(null);
									}}
								>
									<div
										className="rounded-2xl p-6 space-y-4 max-w-md w-full mx-4"
										style={{
											background: "hsl(var(--dash-card-bg))",
											border: "1px solid rgba(245,158,11,0.3)",
										}}
										onClick={(e) => e.stopPropagation()}
									>
										<div className="flex items-start gap-3">
											<div
												className="flex-shrink-0 p-2 rounded-lg"
												style={{ background: "rgba(245,158,11,0.1)" }}
											>
												<AlertTriangle
													className="h-5 w-5"
													style={{ color: "rgb(251,191,36)" }}
												/>
											</div>
											<div className="space-y-1">
												<p
													className="text-sm font-semibold"
													style={{ color: "hsl(var(--dash-text-primary))" }}
												>
													Délai A/B test insuffisant
												</p>
												<p
													className="text-xs"
													style={{ color: "hsl(var(--dash-text-secondary))" }}
												>
													Le délai standard de 24h pour l'A/B test YouTube ne
													peut pas être respecté. Il reste{" "}
													<strong style={{ color: "rgb(251,191,36)" }}>
														{abTestWarning.timeRemaining}
													</strong>{" "}
													avant la première diffusion.
												</p>
											</div>
										</div>
										<div className="space-y-2">
											<button
												onClick={handleAbTestNow}
												className="w-full rounded-xl py-2.5 px-4 text-sm font-semibold transition-all"
												style={{
													background: "hsl(var(--dash-accent))",
													color: "hsl(var(--dash-accent-foreground))",
												}}
											>
												Valider et lancer maintenant
											</button>
											<button
												onClick={handleSkipAbTest}
												className="w-full rounded-xl py-2.5 px-4 text-sm transition-all"
												style={{
													border: "1px solid hsl(var(--dash-border))",
													color: "hsl(var(--dash-text-secondary))",
												}}
											>
												Continuer sans A/B test
											</button>
											<button
												onClick={() => setAbTestWarning(null)}
												className="w-full rounded-xl py-2.5 px-4 text-sm transition-all"
												style={{ color: "hsl(var(--dash-text-tertiary))" }}
											>
												Annuler
											</button>
										</div>
									</div>
								</div>
							)}

							{/* Footer */}
							<div
								className="flex-shrink-0 px-6 py-4 backdrop-blur-sm"
								style={{
									borderTop: "1px solid hsl(var(--dash-border))",
									background: "hsla(var(--dash-bg-base), 0.8)",
								}}
							>
								{validationError && (
									<div
										className="mb-3 p-3 rounded-lg text-xs"
										style={{
											background: "rgba(245,158,11,0.1)",
											border: "1px solid rgba(196,155,59,0.3)",
											color: "rgb(251,191,36)",
										}}
									>
										{validationError}
									</div>
								)}
								<div className="flex gap-3">
									<button
										onClick={onClose}
										className="flex-1 rounded-xl py-2.5 px-4 text-sm transition-all"
										style={{
											border: "1px solid hsl(var(--dash-border))",
											color: "hsl(var(--dash-text-tertiary))",
										}}
									>
										Annuler
									</button>
									<button
										onClick={handleSave}
										disabled={isSaving}
										className="flex-1 rounded-xl py-2.5 px-4 text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
										style={{
											background: "hsl(var(--dash-accent))",
											color: "hsl(var(--dash-accent-foreground))",
										}}
									>
										{isSaving ? (
											<>
												<Loader2 className="h-4 w-4 animate-spin" />
												<span>Enregistrement...</span>
											</>
										) : (
											"Enregistrer"
										)}
									</button>
								</div>
							</div>
						</dialog>
					</motion.div>
				</>
			)}
		</AnimatePresence>,
		document.body,
	);
}
